import { useCallback, useLayoutEffect } from 'react';
import { matchPath } from 'react-router';
import { isEmpty, isEqual, identity } from 'lodash';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useDrawerStack } from '../../../../hooks/queryParams/useDrawerStack';
import { useNavigationBlocker } from './useNavigationBlocker';

export const useUnsavedChangesController = ({
    formPath = '',
    isLastLayer = false,
    hasDataChanges,
    onBeforeConfirmCancelNavigation = identity,
}) => {
    const isMultiLayerDrawerEnabled = useConfigMatch({ [FEATURE_FLAGS.MULTI_LAYER_DRAWER_ENABLED]: 'true' });
    const { stack } = useDrawerStack();

    const skipBlockNavigationCondition = useCallback(
        (pathname, search) => {
            if (isMultiLayerDrawerEnabled) {
                try {
                    const params = new URLSearchParams(search);
                    const newStack = JSON.parse(params.get('drawerStack')) || [];

                    if (newStack.length > stack.length) {
                        return isEqual(stack, newStack.slice(0, newStack.length - 1));
                    }

                    return false;
                } catch (e) {
                    return false;
                }
            }

            return matchPath(pathname, { path: formPath });
        },
        [formPath, isMultiLayerDrawerEnabled, stack]
    );

    const blockNavigation = useNavigationBlocker({ skipBlockNavigationCondition, onBeforeConfirmCancelNavigation });

    // In this effect we activate unsaved changes tracker to prevent user leaving the page with the confirm modal
    useLayoutEffect(() => {
        if (!hasDataChanges || (isMultiLayerDrawerEnabled && !isLastLayer) || isEmpty(formPath)) {
            return;
        }

        const unblockNavigation = blockNavigation();

        return unblockNavigation;
    }, [hasDataChanges, blockNavigation, isLastLayer, isMultiLayerDrawerEnabled, formPath]);
};
